import { useMutation } from "@tanstack/react-query";
import { genericMutation } from "./utils/genericMutation";

/**
 * @template TArg
 * @typedef {{
 * trigger: (arg: TArg) => Promise<*>,
 * isMutating: boolean,
 * data: unknown,
 * error: unknown,
 * reset: () => void
 * }} ActionReturn
 */

/**
 * Hook for performing mutations (POST/PUT/PATCH/DELETE).
 *
 * @template TArg
 * @param {Object} options
 * @param {string | ((arg: TArg) => string)} [options.url] - Static URL or function that receives the trigger arg.
 * @param {"get" | "post" | "put" | "patch" | "delete"} [options.method="post"]
 * @param {(arg: TArg) => Promise<*>} [options.fn] - Override function for custom mutation logic.
 * @returns {ActionReturn<TArg>}
 */
export function useAction({ url, method = "post", fn } = {}) {
	const mutation = useMutation({
		mutationFn: (arg) => {
			if (fn) return fn(arg);
			const resolvedUrl = typeof url === "function" ? url(arg) : url;
			return genericMutation({ url: resolvedUrl, method, payload: arg?.payload });
		},
	});
	return {
		trigger: mutation.mutateAsync,
		isMutating: mutation.isPending,
		data: mutation.data,
		error: mutation.error,
		reset: mutation.reset,
	};
}
